import {
  type FC,
  useMemo,
  useState,
} from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import UserAvatar from '@/components/base/UserAvatar';
import ChangePasswordModal from '@/components/feature/ChangePasswordModal';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import { useAttentionCenter, type AttentionItem } from '@/hooks/useAttentionCenter';

interface TopbarProps {
  onToggleSidebar:
    () => void;

  sidebarCollapsed:
    boolean;
}

const searchTypeLabel:
  Record<
    string,
    string
  > = {
  client:
    'Cliente',

  lead:
    'Lead',

  case:
    'Caso',

  task:
    'Tarefa',

  document:
    'Documento',

  publication:
    'Publicação',

  communication:
    'Comunicação',
};

const attentionIcon:
  Record<
    AttentionItem['source'],
    string
  > = {
  exception:
    'ri-alert-line',

  deadline:
    'ri-timer-flash-line',

  task:
    'ri-checkbox-circle-line',

  communication:
    'ri-message-3-line',

  lead:
    'ri-user-follow-line',

  financial:
    'ri-money-dollar-circle-line',
};

const formatAttentionTime = (
  value:
    string |
    null
) => {
  if (!value) {
    return 'Agora';
  }

  const parsed =
    new Date(
      value
    );

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return '';
  }

  return parsed
    .toLocaleDateString(
      'pt-BR',
      {
        day:
          '2-digit',

        month:
          '2-digit',
      }
    );
};

const Topbar:
  FC<
    TopbarProps
  > = ({
    onToggleSidebar,
    sidebarCollapsed,
  }) => {
  const navigate =
    useNavigate();

  const {
    user,
    logout,
  } =
    useAuth();

  const [
    showNotifications,
    setShowNotifications,
  ] =
    useState(
      false
    );

  const [
    showUserMenu,
    setShowUserMenu,
  ] =
    useState(
      false
    );

  const [
    showPasswordModal,
    setShowPasswordModal,
  ] =
    useState(
      false
    );

  const [
    period,
    setPeriod,
  ] =
    useState<
      'hoje' |
      'semana' |
      'mes'
    >(
      'hoje'
    );

  const [
    search,
    setSearch,
  ] =
    useState(
      ''
    );

  const [
    searchFocused,
    setSearchFocused,
  ] =
    useState(
      false
    );

  const {
    results:
      searchResults,

    loading:
      searchLoading,

    error:
      searchError,
  } =
    useGlobalSearch(
      search
    );

  const {
    items:
      attentionItems,

    summary:
      attentionSummary,

    loading:
      attentionLoading,
  } =
    useAttentionCenter({
      limit:
        8,

    });

  const currentUser =
    useMemo(
      () =>
        user ?? {
          name:
            'Usuário autenticado',

          email:
            'usuario@local',
        },
      [
        user,
      ]
    );

  const currentInitials =
    useMemo(
      () =>
        user
          ? user.name
              .split(
                ' '
              )
              .filter(
                Boolean
              )
              .slice(
                0,
                2
              )
              .map(
                (
                  part
                ) =>
                  part[0]
                    ?.toUpperCase() ??
                  ''
              )
              .join(
                ''
              ) ||
            'BM'
          : 'BM',
      [
        user,
      ]
    );

  const openSearchResult = (
    href:
      string
  ) => {
    setSearchFocused(
      false
    );

    setSearch(
      ''
    );

    navigate(
      href
    );
  };

  return (
    <header className="h-16 bg-background-50 border-b border-background-200 flex items-center justify-between px-4 md:px-6 gap-4">
      <div className="flex items-center gap-3">
        <button
          onClick={onToggleSidebar}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer"
        >
          <i className={`text-lg ${sidebarCollapsed ? 'ri-menu-line' : 'ri-menu-fold-line'}`}></i>
        </button>
      </div>

      <div className="flex-1 max-w-xl hidden md:block relative">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>

          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onFocus={() => setSearchFocused(true)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setSearchFocused(false);
              }

              if (
                event.key === 'Enter' &&
                searchResults[0]
              ) {
                openSearchResult(searchResults[0].href);
              }
            }}
            placeholder="Buscar clientes, leads, casos, tarefas, documentos..."
            className="w-full pl-9 pr-10 py-2 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
          />

          {searchLoading && (
            <i className="ri-loader-4-line animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          )}
        </div>

        {searchFocused && search.trim().length >= 2 && (
          <>
            <button
              type="button"
              aria-label="Fechar busca"
              className="fixed inset-0 z-40 cursor-default"
              onClick={() => setSearchFocused(false)}
            ></button>

            <div className="absolute z-50 left-0 right-0 top-full mt-2 bg-white border border-background-200 rounded-xl shadow-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-background-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-foreground-700">
                  Busca universal
                </span>

                <span className="text-[10px] text-foreground-400">
                  {searchResults.length} resultado(s)
                </span>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {searchError ? (
                  <div className="px-4 py-4 text-xs text-red-600">
                    {searchError}
                  </div>
                ) : !searchLoading && searchResults.length === 0 ? (
                  <div className="px-4 py-5 text-center">
                    <i className="ri-search-eye-line text-xl text-foreground-300"></i>
                    <p className="text-xs text-foreground-500 mt-1">
                      Nenhum resultado encontrado.
                    </p>
                  </div>
                ) : (
                  searchResults.map((result) => (
                    <button
                      key={`${result.type}:${result.id}`}
                      type="button"
                      onClick={() => openSearchResult(result.href)}
                      className="w-full text-left px-4 py-3 border-b border-background-100 hover:bg-primary-50/40 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-800 truncate">
                            {result.title}
                          </p>

                          {result.subtitle && (
                            <p className="text-xs text-foreground-500 mt-0.5 truncate">
                              {result.subtitle}
                            </p>
                          )}
                        </div>

                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 flex-shrink-0">
                          {searchTypeLabel[result.type] ?? result.type}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <button
          onClick={() => navigate('/leads')}
          className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          Novo Lead
        </button>

        <button
          onClick={() => navigate('/clients')}
          className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-team-line"></i>
          Novo Cliente
        </button>

        <button
          onClick={() => navigate('/cases')}
          className="hidden xl:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Novo Caso
        </button>

        <select
          value={period}
          onChange={(event) => setPeriod(event.target.value as typeof period)}
          className="text-xs px-2.5 py-1.5 bg-background-100 border border-background-200 rounded-lg text-foreground-600 focus:outline-none focus:border-primary-300 cursor-pointer hidden md:block"
        >
          <option value="hoje">Hoje</option>
          <option value="semana">Semana</option>
          <option value="mes">Mês</option>
        </select>

        <div className="relative">
          <button
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowUserMenu(false);
            }}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer relative"
            title="Central de Atenção"
          >
            <i className="ri-notification-3-line text-lg"></i>

            {attentionSummary.total > 0 && (
              <span className={`absolute top-0.5 right-0.5 min-w-4 h-4 px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center ${
                attentionSummary.critical > 0
                  ? 'bg-red-500'
                  : 'bg-amber-500'
              }`}>
                {attentionSummary.total > 99 ? '99+' : attentionSummary.total}
              </span>
            )}
          </button>

          {showNotifications && (
            <>
              <button
                type="button"
                aria-label="Fechar notificações"
                className="fixed inset-0 z-40 cursor-default"
                onClick={() => setShowNotifications(false)}
              ></button>

              <div className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-background-50 border border-background-200 rounded-xl z-50 overflow-hidden shadow-lg">
                <div className="px-4 py-3 border-b border-background-200 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">
                      Central de Atenção
                    </p>

                    <p className="text-[10px] text-foreground-400">
                      {attentionSummary.critical} crítico(s) · {attentionSummary.important} importante(s)
                    </p>
                  </div>

                  <span className="text-[10px] text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">
                    {attentionSummary.total} item(ns)
                  </span>
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {attentionLoading && attentionItems.length === 0 ? (
                    <div className="px-4 py-5 text-xs text-foreground-500 text-center">
                      Carregando pendências reais...
                    </div>
                  ) : attentionItems.length === 0 ? (
                    <div className="px-4 py-5 text-center">
                      <i className="ri-checkbox-circle-line text-xl text-emerald-500"></i>
                      <p className="text-xs text-foreground-500 mt-1">
                        Nenhuma pendência relevante.
                      </p>
                    </div>
                  ) : (
                    attentionItems.slice(0, 6).map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setShowNotifications(false);
                          navigate(item.href);
                        }}
                        className="w-full text-left px-4 py-3 border-b border-background-100 hover:bg-background-50 transition-colors"
                      >
                        <div className="flex items-start gap-2.5">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            item.severity === 'critical'
                              ? 'bg-red-50 text-red-600'
                              : item.severity === 'important'
                                ? 'bg-amber-50 text-amber-600'
                                : 'bg-blue-50 text-blue-600'
                          }`}>
                            <i className={attentionIcon[item.source]}></i>
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-foreground-700 line-clamp-2">
                              {item.title}
                            </p>

                            {item.detail && (
                              <p className="text-[10px] text-foreground-400 mt-0.5 line-clamp-1">
                                {item.detail}
                              </p>
                            )}

                            <p className="text-[10px] text-foreground-400 mt-1">
                              {formatAttentionTime(item.dueAt ?? item.occurredAt)}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                <div className="px-4 py-2.5 border-t border-background-200 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNotifications(false);
                      navigate('/attention');
                    }}
                    className="text-xs text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    Ver Central de Atenção completa
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotifications(false);
            }}
            className="flex items-center gap-2 cursor-pointer"
          >
            <UserAvatar
              initials={currentInitials}
              name={currentUser.name}
              size="sm"
            />
          </button>

          {showUserMenu && (
            <>
              <button
                type="button"
                aria-label="Fechar menu do usuário"
                className="fixed inset-0 z-40 cursor-default"
                onClick={() => setShowUserMenu(false)}
              ></button>

              <div className="absolute right-0 top-full mt-2 w-56 bg-background-50 border border-background-200 rounded-xl z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-background-200">
                  <p className="text-sm font-semibold text-foreground-800">
                    {currentUser.name}
                  </p>

                  <p className="text-xs text-foreground-400">
                    {currentUser.email}
                  </p>

                  <p className="text-[10px] text-foreground-400 mt-0.5">
                    Sessão autenticada
                  </p>
                </div>

                <div className="py-1">
                  <button
                    onClick={() => navigate('/settings')}
                    className="w-full text-left px-4 py-2 text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-user-settings-line"></i>
                    Perfil
                  </button>

                  <button
                    onClick={() => navigate('/settings')}
                    className="w-full text-left px-4 py-2 text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-settings-3-line"></i>
                    Configurações
                  </button>
                </div>

                <div className="border-t border-background-200 py-1">
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowPasswordModal(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-lock-password-line"></i>
                    Alterar senha
                  </button>

                  <button
                    onClick={async () => {
                      await logout();
                      navigate('/login');
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-logout-box-line"></i>
                    Sair
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </header>
  );
};

export default Topbar;
